import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineClipboardDocumentCheck,
  HiOutlineCube,
  HiOutlineCheckCircle,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineClipboardDocumentList
} from 'react-icons/hi2';
import { authService, sparePartsService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import { usePermissions } from '../../../hooks/usePermissions';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import '../PlannedMaintenanceMasters.css';

const FREQUENCY_TYPES = ['Time', 'Meter', 'Condition', 'Hybrid'];
const FREQUENCY_UNITS = ['Days', 'Weeks', 'Months', 'Years', 'Hours', 'Cycles', 'Counts',];

// Synchronous cache loading function
const getCachedMaintenancePlansData = () => {
  try {
    const cachedPlans = sessionStorage.getItem('maintenance_plans_data');
    const cachedSkills = sessionStorage.getItem('technician_skills_data');
    const cachedPriorities = sessionStorage.getItem('work_order_priorities_data');
    const cachedSpareParts = sessionStorage.getItem('spare_parts_data');
    const cachedCategories = sessionStorage.getItem('pm_categories_data');
    
    return {
      plans: cachedPlans ? JSON.parse(cachedPlans) : [],
      skills: cachedSkills ? JSON.parse(cachedSkills) : [],
      priorities: cachedPriorities ? JSON.parse(cachedPriorities) : [],
      spareParts: cachedSpareParts ? JSON.parse(cachedSpareParts) : [],
      categories: cachedCategories ? JSON.parse(cachedCategories) : [],
      hasAll: !!(cachedPlans && cachedSkills && cachedPriorities && cachedSpareParts && cachedCategories)
    };
  } catch (e) {
    console.error('Error parsing cached Maintenance Plans data:', e);
    return { plans: [], skills: [], priorities: [], spareParts: [], categories: [], hasAll: false };
  }
};

const MaintenancePlansPage = () => {
  // Initialize state with cached data if available (synchronous)
  const cachedInitialData = getCachedMaintenancePlansData();
  const [plans, setPlans] = useState(cachedInitialData.plans);
  const [skills, setSkills] = useState(cachedInitialData.skills);
  const [priorities, setPriorities] = useState(cachedInitialData.priorities);
  const [spareParts, setSpareParts] = useState(cachedInitialData.spareParts);
  const [categories, setCategories] = useState(cachedInitialData.categories);
  const [loading, setLoading] = useState(!cachedInitialData.hasAll);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planParts, setPlanParts] = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  const [partsError, setPartsError] = useState('');
  const [newPart, setNewPart] = useState({ sparePartId: '', quantity: '1', uom: '', notes: '' });
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planTasks, setPlanTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState('');
  const [formData, setFormData] = useState({
    planCode: '',
    planName: '',
    description: '',
    assetCategoryId: '',
    frequencyType: '',
    frequencyValue: '',
    frequencyUnit: '',
    estimatedDurationMinutes: '',
    estimatedLaborHours: '',
    requiredSkillId: '',
    defaultPriorityId: '',
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission('PlannedMaintenance.View');
  const canCreate = hasPermission('PlannedMaintenance.Create');
  const canEdit = hasPermission('PlannedMaintenance.Edit');
  const canDelete = hasPermission('PlannedMaintenance.Delete');

  console.log('canView', canView);
  console.log('canCreate', canCreate);
  console.log('canEdit', canEdit);
  console.log('canDelete', canDelete);

  useEffect(() => {
    if (tenantId && canView) {
      // Load fresh data in background if we have cached data, otherwise show loading
      const hasCachedData = cachedInitialData.hasAll;
      loadData(!hasCachedData);
    } else if (!canView) {
      setError('You do not have permission to view Planned Maintenance records');
      setLoading(false);
    } else {
      setError('Please login to view maintenance plans');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, canView]);

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      const [plansData, skillsData, prioritiesData, partsData, categoriesData] = await Promise.all([
        masterDataService.getMaintenancePlans(tenantId),
        masterDataService.getTechnicianSkills(tenantId),
        masterDataService.getWorkOrderPriorities(tenantId),
        sparePartsService.getParts(),
        masterDataService.getCategories(tenantId)
      ]);
      setPlans(plansData || []);
      setSkills(skillsData || []);
      setPriorities(prioritiesData || []);
      setSpareParts(partsData || []);
      setCategories(categoriesData || []);
      
      // Cache the data
      sessionStorage.setItem('maintenance_plans_data', JSON.stringify(plansData || []));
      sessionStorage.setItem('technician_skills_data', JSON.stringify(skillsData || []));
      sessionStorage.setItem('work_order_priorities_data', JSON.stringify(prioritiesData || []));
      sessionStorage.setItem('spare_parts_data', JSON.stringify(partsData || []));
      // Filter categories for PM if needed
      const pmCategories = (categoriesData || []).filter(c => !c.categoryType || c.categoryType === 'PM' || c.categoryType === 'Asset');
      sessionStorage.setItem('pm_categories_data', JSON.stringify(pmCategories));
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading data:', err);
      // Don't clear cached data on error, keep showing it
      if (!cachedInitialData.hasAll) {
        setPlans([]);
        setSkills([]);
        setPriorities([]);
        setSpareParts([]);
        setCategories([]);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const loadPlanParts = async (planId) => {
    try {
      setPartsLoading(true);
      setPartsError('');
      const data = await masterDataService.getMaintenancePlanSpareParts(tenantId, planId);
      setPlanParts(data || []);
    } catch (err) {
      setPartsError('Failed to load recommended spare parts.');
      console.error('Error loading plan spare parts:', err);
    } finally {
      setPartsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredPlans = plans.filter((plan) =>
    plan.planCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.planName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    if (!canCreate) {
      setError('You do not have permission to create Planned Maintenance records');
      return;
    }
    setEditingPlan(null);
    setFormData({
      planCode: '',
      planName: '',
      description: '',
      assetCategoryId: '',
      frequencyType: '',
      frequencyValue: '',
      frequencyUnit: '',
      estimatedDurationMinutes: '',
      estimatedLaborHours: '',
      requiredSkillId: '',
      defaultPriorityId: '',
      isActive: true,
    });
    setPlanParts([]);
    setPartsError('');
    setNewPart({ sparePartId: '', quantity: '1', uom: '', notes: '' });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    if (!canEdit) {
      setError('You do not have permission to edit Planned Maintenance records');
      return;
    }
    setEditingPlan(plan);
    setFormData({
      planCode: plan.planCode || '',
      planName: plan.planName || '',
      description: plan.description || '',
      assetCategoryId: plan.assetCategoryId?.toString() || '',
      frequencyType: plan.frequencyType || '',
      frequencyValue: plan.frequencyValue?.toString() || '',
      frequencyUnit: plan.frequencyUnit || '',
      estimatedDurationMinutes: plan.estimatedDurationMinutes?.toString() || '',
      estimatedLaborHours: plan.estimatedLaborHours?.toString() || '',
      requiredSkillId: plan.requiredSkillId?.toString() || '',
      defaultPriorityId: plan.defaultPriorityId?.toString() || '',
      isActive: plan.isActive !== undefined ? plan.isActive : true,
    });
    setNewPart({ sparePartId: '', quantity: '1', uom: '', notes: '' });
    setPlanParts([]);
    setPartsError('');
    loadPlanParts(plan.maintenancePlanId);
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      setError('You do not have permission to delete Planned Maintenance records');
      return;
    }
    try {
      setError('');
      await masterDataService.deleteMaintenancePlan(id);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete maintenance plan. Please try again.');
      console.error('Error deleting maintenance plan:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.planCode.trim()) {
      errors.planCode = 'Plan code is required';
    }
    if (!formData.planName.trim()) {
      errors.planName = 'Plan name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check permissions before submitting
    if (editingPlan && !canEdit) {
      setError('You do not have permission to edit Planned Maintenance records');
      return;
    }
    if (!editingPlan && !canCreate) {
      setError('You do not have permission to create Planned Maintenance records');
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const planData = {
        tenantId: tenantId,
        planCode: formData.planCode.trim(),
        planName: formData.planName.trim(),
        description: formData.description.trim() || null,
        assetCategoryId: formData.assetCategoryId ? parseInt(formData.assetCategoryId) : null,
        frequencyType: formData.frequencyType.trim() || null,
        frequencyValue: formData.frequencyValue ? parseInt(formData.frequencyValue) : null,
        frequencyUnit: formData.frequencyUnit.trim() || null,
        estimatedDurationMinutes: formData.estimatedDurationMinutes ? parseInt(formData.estimatedDurationMinutes) : null,
        estimatedLaborHours: formData.estimatedLaborHours ? parseFloat(formData.estimatedLaborHours) : null,
        requiredSkillId: formData.requiredSkillId ? parseInt(formData.requiredSkillId) : null,
        defaultPriorityId: formData.defaultPriorityId ? parseInt(formData.defaultPriorityId) : null,
        isActive: formData.isActive,
      };

      if (editingPlan) {
        planData.maintenancePlanId = editingPlan.maintenancePlanId;
        await masterDataService.updateMaintenancePlan(editingPlan.maintenancePlanId, planData);
      } else {
        await masterDataService.createMaintenancePlan(planData);
      }

      await loadData();
      setShowModal(false);
      setFormData({ planCode: '', planName: '', description: '', assetCategoryId: '', frequencyType: '', frequencyValue: '', frequencyUnit: '', estimatedDurationMinutes: '', estimatedLaborHours: '', requiredSkillId: '', defaultPriorityId: '', isActive: true });
      setEditingPlan(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save maintenance plan. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPlan(null);
    setFormData({ planCode: '', planName: '', description: '', assetCategoryId: '', frequencyType: '', frequencyValue: '', frequencyUnit: '', estimatedDurationMinutes: '', estimatedLaborHours: '', requiredSkillId: '', defaultPriorityId: '', isActive: true });
    setPlanParts([]);
    setPartsError('');
    setNewPart({ sparePartId: '', quantity: '1', uom: '', notes: '' });
    setFormErrors({});
  };

  const handleAddPart = async () => {
    if (!editingPlan?.maintenancePlanId) {
      setPartsError('Please save the plan first before adding spare parts.');
      return;
    }
    if (!newPart.sparePartId) {
      setPartsError('Please select a spare part.');
      return;
    }
    const qty = Number(newPart.quantity);
    if (!qty || qty <= 0) {
      setPartsError('Quantity must be greater than 0.');
      return;
    }
    try {
      setPartsError('');
      await masterDataService.createMaintenancePlanSparePart({
        tenantId,
        maintenancePlanId: editingPlan.maintenancePlanId,
        sparePartId: parseInt(newPart.sparePartId, 10),
        quantity: qty,
        uom: newPart.uom.trim() || null,
        notes: newPart.notes.trim() || null,
        status: 'Active',
      });
      setNewPart({ sparePartId: '', quantity: '1', uom: '', notes: '' });
      await loadPlanParts(editingPlan.maintenancePlanId);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to add spare part.';
      setPartsError(message);
    }
  };

  const handleRemovePart = async (planSparePartId) => {
    try {
      setPartsError('');
      await masterDataService.deleteMaintenancePlanSparePart(planSparePartId);
      await loadPlanParts(editingPlan.maintenancePlanId);
    } catch (err) {
      setPartsError('Failed to remove spare part.');
    }
  };

  const handleRowClick = async (plan) => {
    setSelectedPlan(plan);
    setShowTasksModal(true);
    await loadPlanTasks(plan.maintenancePlanId);
  };

  const loadPlanTasks = async (planId) => {
    try {
      setTasksLoading(true);
      setTasksError('');
      const data = await masterDataService.getMaintenancePlanTasks(tenantId, planId);
      setPlanTasks(data || []);
    } catch (err) {
      setTasksError('Failed to load tasks.');
      console.error('Error loading plan tasks:', err);
    } finally {
      setTasksLoading(false);
    }
  };

  const handleCloseTasksModal = () => {
    setShowTasksModal(false);
    setSelectedPlan(null);
    setPlanTasks([]);
    setTasksError('');
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }
    try {
      setTasksError('');
      await masterDataService.deleteMaintenancePlanTask(taskId);
      await loadPlanTasks(selectedPlan.maintenancePlanId);
    } catch (err) {
      setTasksError('Failed to delete task.');
    }
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading maintenance plans..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Maintenance Plans</h1>
          <p>Manage maintenance plans for Planned Maintenance</p>
        </div>
        {canCreate && (
          <button className="btn-primary" onClick={handleAdd}>
            <HiOutlinePlus />
            Add Plan
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search plans..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredPlans.length} {filteredPlans.length === 1 ? 'plan' : 'plans'}
          </div>
        </div>

        {filteredPlans.length === 0 ? (
          <div className="empty-state">
            <HiOutlineClipboardDocumentCheck className="empty-icon" />
            <h3>No plans found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : canCreate
                ? 'Get started by creating your first maintenance plan'
                : 'No maintenance plans found'}
            </p>
            {!searchTerm && canCreate && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Plan
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Value</th>
                <th>Unit</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlans.map((plan) => (
                <tr 
                  key={plan.maintenancePlanId}
                  onClick={() => handleRowClick(plan)}
                  style={{ cursor: 'pointer' }}
                  className="clickable-row"
                >
                  <td>{plan.maintenancePlanId}</td>
                  <td>{plan.planCode}</td>
                  <td className="name-cell">{plan.planName}</td>
                  <td>{plan.frequencyType || '-'}</td>
                  <td>{plan.frequencyValue ?? '-'}</td>
                  <td>{plan.frequencyUnit || '-'}</td>
                  <td>{plan.isActive ? 'Yes' : 'No'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="action-buttons">
                      {canEdit && (
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleEdit(plan)}
                          title="Edit"
                        >
                          <HiOutlinePencil />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(plan)}
                          title="Delete"
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingPlan ? 'Edit Maintenance Plan' : 'Add New Maintenance Plan'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="maintenance-plan-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingPlan
                  ? 'Updating...'
                  : 'Creating...'
                : editingPlan
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
        customWidth="800px"
      >
        <div className="shared-modal-body">
          <form id="maintenance-plan-form" onSubmit={handleSubmit} className="modal-form two-column">
            <div className="form-group">
              <label htmlFor="planCode">
                Plan Code <span className="required">*</span>
              </label>
              <input
                type="text"
                id="planCode"
                value={formData.planCode}
                onChange={(e) =>
                  setFormData({ ...formData, planCode: e.target.value })
                }
                className={formErrors.planCode ? 'error' : ''}
                placeholder="Enter plan code"
              />
              {formErrors.planCode && (
                <span className="field-error">{formErrors.planCode}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="planName">
                Plan Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="planName"
                value={formData.planName}
                onChange={(e) =>
                  setFormData({ ...formData, planName: e.target.value })
                }
                className={formErrors.planName ? 'error' : ''}
                placeholder="Enter plan name"
              />
              {formErrors.planName && (
                <span className="field-error">{formErrors.planName}</span>
              )}
            </div>

            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter description (optional)"
                rows="2"
              />
            </div>

            <div className="form-group">
              <label htmlFor="frequencyType">Frequency Type</label>
              <select
                id="frequencyType"
                value={formData.frequencyType}
                onChange={(e) =>
                  setFormData({ ...formData, frequencyType: e.target.value })
                }
              >
                <option value="">Select Frequency Type</option>
                {FREQUENCY_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="frequencyValue">Frequency Value</label>
              <input
                type="number"
                id="frequencyValue"
                value={formData.frequencyValue}
                onChange={(e) =>
                  setFormData({ ...formData, frequencyValue: e.target.value })
                }
                placeholder="Enter frequency value (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="frequencyUnit">Frequency Unit</label>
              <select
                id="frequencyUnit"
                value={formData.frequencyUnit}
                onChange={(e) =>
                  setFormData({ ...formData, frequencyUnit: e.target.value })
                }
              >
                <option value="">Select Frequency Unit</option>
                {FREQUENCY_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="estimatedDurationMinutes">Estimated Duration (min)</label>
              <input
                type="number"
                id="estimatedDurationMinutes"
                value={formData.estimatedDurationMinutes}
                onChange={(e) =>
                  setFormData({ ...formData, estimatedDurationMinutes: e.target.value })
                }
                placeholder="Enter estimated duration in minutes (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="estimatedLaborHours">Estimated Labor (hrs)</label>
              <input
                type="number"
                id="estimatedLaborHours"
                value={formData.estimatedLaborHours}
                onChange={(e) =>
                  setFormData({ ...formData, estimatedLaborHours: e.target.value })
                }
                placeholder="Enter estimated labor hours (optional)"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label htmlFor="requiredSkillId">Required Skill</label>
              <select
                id="requiredSkillId"
                value={formData.requiredSkillId}
                onChange={(e) =>
                  setFormData({ ...formData, requiredSkillId: e.target.value })
                }
              >
                <option value="">Select Skill (Optional)</option>
                {skills.map((skill) => (
                  <option key={skill.skillId} value={skill.skillId.toString()}>
                    {skill.skillName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="defaultPriorityId">Default Priority</label>
              <select
                id="defaultPriorityId"
                value={formData.defaultPriorityId}
                onChange={(e) =>
                  setFormData({ ...formData, defaultPriorityId: e.target.value })
                }
              >
                <option value="">Select Priority (Optional)</option>
                {priorities.map((priority) => (
                  <option key={priority.priorityId} value={priority.priorityId.toString()}>
                    {priority.priorityName}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              backgroundColor: '#f8fafc',
              padding: '12px 16px',
              borderRadius: '12px',
              border: '1px solid #e2e8f0; border-color: var(--border-primary)',
              marginTop: '0.5rem',
              gridColumn: 'span 2'
            }}>
              <span style={{ 
                fontWeight: 700, 
                color: '#475569', 
                fontSize: '12px', 
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                Active Status
              </span>
              <label className="switch-pm">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
                <span className="slider-pm"></span>
              </label>
            </div>
          </form>

          {/* Recommended Spare Parts Section - used when editing */}
          {editingPlan && (
             <div style={{ marginTop: '2rem', borderTop: '2px solid #f1f5f9', paddingTop: '1.5rem' }}>
                <h4 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px', color: '#1e293b' }}>
                  <HiOutlineCube /> Recommended Spare Parts
                </h4>
                
                {partsError && <div className="error-message" style={{ marginBottom: '1rem' }}>{partsError}</div>}
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '2fr 1fr 1fr 2fr auto', 
                  gap: '10px', 
                  alignItems: 'end',
                  marginBottom: '1rem',
                  backgroundColor: '#f8fafc',
                  padding: '1rem',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0; border-color: var(--border-primary)'
                }}>
                  <div className="form-group">
                    <label style={{ fontSize: '11px' }}>Spare Part</label>
                    <select
                      value={newPart.sparePartId}
                      onChange={(e) => setNewPart({ ...newPart, sparePartId: e.target.value })}
                    >
                      <option value="">Select Part</option>
                      {spareParts.map(part => (
                        <option key={part.sparePartId} value={part.sparePartId}>
                          {part.partCode} - {part.partName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '11px' }}>Qty</label>
                    <input
                      type="number"
                      value={newPart.quantity}
                      onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                      min="1"
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '11px' }}>UOM</label>
                    <input
                      type="text"
                      value={newPart.uom}
                      onChange={(e) => setNewPart({ ...newPart, uom: e.target.value })}
                      placeholder="e.g. Pcs"
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '11px' }}>Notes</label>
                    <input
                      type="text"
                      value={newPart.notes}
                      onChange={(e) => setNewPart({ ...newPart, notes: e.target.value })}
                      placeholder="Optional notes"
                    />
                  </div>
                  <button 
                    type="button" 
                    className="btn-primary" 
                    onClick={handleAddPart}
                    style={{ height: '38px', padding: '0 15px' }}
                  >
                    Add
                  </button>
                </div>

                <div className="report-table-wrapper" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {partsLoading ? (
                    <div style={{ textAlign: 'center', padding: '1rem' }}>Loading parts...</div>
                  ) : planParts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '1rem', color: '#94a3b8', fontSize: '13px' }}>
                      No recommended spare parts defined for this plan.
                    </div>
                  ) : (
                    <table className="report-table" style={{ fontSize: '13px' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                          <th>Part Code / Name</th>
                          <th>Quantity</th>
                          <th>UOM</th>
                          <th>Notes</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {planParts.map(item => (
                          <tr key={item.planSparePartId}>
                            <td>
                              <strong>{item.partCode}</strong>
                              <div style={{ fontSize: '11px', color: '#64748b' }}>{item.partName}</div>
                            </td>
                            <td>{item.quantity}</td>
                            <td>{item.uom || '-'}</td>
                            <td>{item.notes || '-'}</td>
                            <td>
                              <button 
                                className="btn-icon-delete" 
                                onClick={() => handleRemovePart(item.planSparePartId)}
                                style={{ width: '28px', height: '28px' }}
                              >
                                <HiOutlineTrash />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
             </div>
          )}
        </div>
      </SharedModal>

      {/* Tasks Modal */}
      <SharedModal
        isOpen={showTasksModal}
        onClose={handleCloseTasksModal}
        title={selectedPlan ? `Tasks for: ${selectedPlan.planName}` : 'Plan Tasks'}
        customWidth="900px"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={handleCloseTasksModal}>Close</button>
          </>
        }
      >
        <div className="shared-modal-body">
          {selectedPlan && (
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0; border-color: var(--border-primary)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Plan Code</label>
                <div style={{ fontWeight: 600 }}>{selectedPlan.planCode}</div>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Frequency</label>
                <div style={{ fontWeight: 600 }}>{selectedPlan.frequencyValue} {selectedPlan.frequencyUnit} ({selectedPlan.frequencyType})</div>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Description</label>
                <div style={{ fontSize: '13px' }}>{selectedPlan.description || 'No description provided.'}</div>
              </div>
            </div>
          )}

          {tasksError && <div className="error-message" style={{ marginBottom: '1rem' }}>{tasksError}</div>}

          {tasksLoading ? (
            <div className="empty-state" style={{ padding: '3rem' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 1rem' }}></div>
              <p>Loading tasks...</p>
            </div>
          ) : planTasks.length === 0 ? (
            <div className="empty-state" style={{ padding: '3rem' }}>
              <HiOutlineClipboardDocumentList className="empty-icon" />
              <h3>No tasks defined</h3>
              <p>There are no tasks associated with this maintenance plan.</p>
            </div>
          ) : (
            <div className="report-table-wrapper">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Sequence</th>
                    <th>Task Description</th>
                    <th>Ref Code</th>
                    <th>Estimated Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {planTasks.map((task) => (
                    <tr key={task.planTaskId}>
                      <td style={{ fontWeight: 700, color: '#6366f1' }}>{task.taskSequence}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{task.taskDescription}</div>
                        {task.standardHow && (
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                            <strong>Method:</strong> {task.standardHow}
                          </div>
                        )}
                      </td>
                      <td>{task.referenceCode || '-'}</td>
                      <td>{task.estimatedMinutes ? `${task.estimatedMinutes} min` : '-'}</td>
                      <td>
                        <button
                          className="btn-icon-delete"
                          onClick={() => handleDeleteTask(task.planTaskId)}
                          title="Delete Task"
                        >
                          <HiOutlineTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SharedModal>
      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.maintenancePlanId)}
            >
              Delete
            </button>
          </>
        }
        customWidth="400px"
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.planName}</strong>?
          </p>
          <p className="warning-text" style={{ color: 'var(--status-error)', marginTop: '0.5rem' }}>
            This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default MaintenancePlansPage;
